import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { toast } from 'sonner';
import { History, Link2 } from 'lucide-react';
import { fmtDate } from '../lib/date';
import { formatPhoneDisplay } from '../lib/phone';

const Row = ({ label, value }) => (value ? (
  <div className="text-[12px]"><span className="text-[#8A7B95] uppercase tracking-wider text-[10px] font-semibold mr-1.5">{label}</span>{value}</div>
) : null);

const EarlierCard = ({ e, label }) => (
  <div className={`rounded-xl border p-4 space-y-1.5 ${e.linked ? 'border-[#685B72]/40 bg-[#F4F2F7]' : 'border-[#E8E5EC] bg-white'}`}
    data-testid={`earlier-inquiry-${e.inquiry_id}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="font-semibold text-[#2D2833]">{e.client_name || e.form_submitter || 'Unnamed'}</div>
      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E8E5EC] text-[#685B72]"
        data-testid={`earlier-disposition-${e.inquiry_id}`}>{label(e.disposition)}</span>
    </div>
    {e.linked && <div className="text-[11px] text-[#685B72] flex items-center gap-1"><Link2 size={11} /> This is the inquiry the badge points to</div>}
    <Row label="Received" value={fmtDate(e.received_iso || e.received_at)} />
    <Row label="Submitted by" value={e.form_submitter} />
    <Row label="Phone" value={formatPhoneDisplay(e.phone)} />
    <Row label="Email" value={e.email} />
    <Row label="Insurance" value={e.insurance} />
    <Row label="Clinic" value={e.clinic} />
    <Row label="Referral" value={e.referral_source} />
    <Row label="Outcome notes" value={e.disposition_notes} />
    <Row label="Form notes" value={e.notes} />
    <Row label="Contact attempts" value={(e.contact_attempts || []).length || null} />
    <Row label="Converted to client" value={e.converted_client_id} />
    <Row label="Last updated" value={e.updated_at ? `${fmtDate(e.updated_at)}${e.updated_by ? ` by ${e.updated_by}` : ''}` : null} />
  </div>
);

export const EarlierInquiriesSheet = ({ inquiry, onClose, label }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!inquiry) return;
    setItems(null);
    api.get(`/inquiries/${inquiry.inquiry_id}/earlier`)
      .then((r) => setItems(r.data.earlier || []))
      .catch((e) => { toast.error(e?.response?.data?.detail || 'Could not load earlier inquiries'); setItems([]); });
  }, [inquiry]);

  return (
    <Sheet open={!!inquiry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="earlier-inquiries-sheet">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[#2D2833] flex items-center gap-2"><History size={18} /> Earlier inquiries</SheetTitle>
        </SheetHeader>
        <p className="text-[12px] text-[#685B72] mt-1 mb-4">
          Same family as <b>{inquiry?.client_name || inquiry?.form_submitter}</b> (matched by phone and child), newest first.
        </p>
        {items === null && <div className="text-sm text-[#685B72]">Loading…</div>}
        {items?.length === 0 && <div className="text-sm text-[#685B72]" data-testid="earlier-empty">No earlier inquiries found for this family.</div>}
        <div className="space-y-3">{(items || []).map((e) => <EarlierCard key={e.inquiry_id} e={e} label={label} />)}</div>
      </SheetContent>
    </Sheet>
  );
};

export default EarlierInquiriesSheet;
